
import { Task } from '@/types/task';

export const exportTasksToCSV = (tasks: Task[]) => {
  // Filter out any null or undefined tasks
  const validTasks = tasks.filter(task => task && task.title);
  
  const csvData = validTasks.map(task => ({
    title: task.title,
    description: task.description || '',
    hebrewTitle: task.hebrewTitle || '',
    hebrewDescription: task.hebrewDescription || '',
    isCompleted: task.isCompleted,
    scheduledDay: task.scheduledDay || '',
    scheduledHour: task.scheduledHour || '',
    scheduledMinute: task.scheduledMinute || ''
  }));

  // Only proceed if there are tasks to export
  if (csvData.length === 0) {
    console.log('No tasks to export');
    return;
  }

  const headers = ['title', 'description', 'hebrewTitle', 'hebrewDescription', 'isCompleted', 'scheduledDay', 'scheduledHour', 'scheduledMinute'];
  const csvContent = [
    headers.join(','),
    ...csvData.map(row => 
      headers.map(header => {
        const value = row[header as keyof typeof row];
        // Handle boolean values explicitly
        if (typeof value === 'boolean') {
          return value.toString();
        }
        // Handle string values with potential commas
        if (typeof value === 'string') {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'tasks.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const parseCSVTasks = (text: string) => {
  const lines = text.split('\n');
  if (lines.length < 2) {
    console.log('No tasks to import');
    return [];
  }

  const headers = lines[0].split(',').map(h => h.trim());
  
  const cleanValue = (value: string) => {
    // Remove quotes and handle escaped quotes
    if (value.startsWith('"') && value.endsWith('"')) {
      return value.slice(1, -1).replace(/""/g, '"').trim();
    }
    return value.trim();
  };

  return lines.slice(1).filter(line => line.trim()).map(line => {
    const values = line.split(',').map(cleanValue);
    
    const task: any = {};
    headers.forEach((header, index) => {
      let value = values[index];
      
      // Clean up the value and handle "null" or empty strings
      if (value === 'null' || value === '' || value === '""') {
        value = null;
      }
      
      switch (header) {
        case 'isCompleted':
          task[header] = String(value).toLowerCase() === 'true';
          break;

        case 'scheduledDay':
          const validDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          // If the value is not a valid day name, set it to null (unscheduled/"To Do This Week")
          task[header] = value && validDays.includes(value) ? value : null;
          break;

        case 'scheduledHour':
          if (value !== null) {
            const hour = parseInt(value, 10);
            task[header] = !isNaN(hour) && hour >= 0 && hour <= 23 ? hour : null;
          } else {
            task[header] = null;
          }
          break;

        case 'scheduledMinute':
          if (value !== null) {
            const minute = parseInt(value, 10);
            task[header] = !isNaN(minute) && minute >= 0 && minute <= 59 ? minute : null;
          } else {
            task[header] = null;
          }
          break;

        default:
          task[header] = value || null;
      }
    });
    
    // If scheduledDay is falsy (empty string, false, etc), explicitly set it to null
    if (!task.scheduledDay) {
      task.scheduledDay = null;
    }
    
    // Only return tasks that have at least a title
    return task.title ? task : null;
  }).filter(task => task !== null);
};
